import { UIEvent, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { valueForOnlyNumericInputTextFields } from "utils/helpers";

import {
  selectIsAddModalOpen,
  selectSuperModelForm,
  selectSuperModelFormDropDown,
  selectSuperModelSaving,
} from "../redux/selector";
import { superModelActions } from "../redux/slice";
import {
  SUPER_MODEL_FORM_FIELD_KINDS,
  SuperModelForm,
  SuperModelSelectOption,
} from "../types";

export type SuperModelFieldConfig = {
  key: keyof SuperModelForm;
  label: string;
  type: "select" | "text";
  options?: SuperModelSelectOption[];
};

export const sanitizeSuperModelFormValue = (
  name: keyof SuperModelForm,
  value: string,
) => {
  if (SUPER_MODEL_FORM_FIELD_KINDS[name] === "number") {
    return valueForOnlyNumericInputTextFields(value);
  }
  return value;
};

const P = "SUPER_MODEL.MODAL";

export const useAddSuperModelModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const open = useSelector(selectIsAddModalOpen);
  const form = useSelector(selectSuperModelForm);
  const saving = useSelector(selectSuperModelSaving);
  const formDropDown = useSelector(selectSuperModelFormDropDown);

  useEffect(() => {
    if (!open) return;
    dispatch(superModelActions.getFormDropDownList());
  }, [open, dispatch]);

  const manufacturerSelectOptions = useMemo(
    () =>
      formDropDown.items.map(({ id, name }) => ({
        value: String(id),
        label: name,
      })),
    [formDropDown.items],
  );

  const fields: SuperModelFieldConfig[] = useMemo(
    () => [
      { key: "name", label: "NAME", type: "text" },
      {
        key: "manufacturer",
        label: "MANUFACTURER",
        type: "select",
        options: manufacturerSelectOptions,
      },
      { key: "priority", label: "PRIORITY", type: "text" },
    ],
    [manufacturerSelectOptions],
  );

  const onClose = () => {
    if (saving) return;
    dispatch(superModelActions.closeAddModal());
  };

  const setField = (name: keyof SuperModelForm, value: string) =>
    dispatch(
      superModelActions.setFormField({
        name,
        value: sanitizeSuperModelFormValue(name, value),
      }),
    );

  const placeholder = (label: string, type: SuperModelFieldConfig["type"]) =>
    t(`${P}.PLACEHOLDERS.${type === "select" ? "SELECT" : "ENTER"}_${label}`);

  const fieldLabel = (label: string) => t(`${P}.FIELDS.${label}`);

  const optionLabel = (option: SuperModelSelectOption) =>
    option.labelKey ? t(option.labelKey) : option.label || option.value;

  const isValid =
    Boolean(form.name.trim()) &&
    Boolean(form.manufacturer.trim()) &&
    Boolean(form.priority.trim());

  const isNumberField = (key: keyof SuperModelForm) =>
    SUPER_MODEL_FORM_FIELD_KINDS[key] === "number";

  const hasMoreManufacturerItems =
    !formDropDown.loading &&
    formDropDown.meta.currentPage < formDropDown.meta.totalPages;

  const onManufacturerMenuScroll = (event: UIEvent<HTMLElement>) => {
    if (!hasMoreManufacturerItems) return;

    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollHeight - (scrollTop + clientHeight) <= 2) {
      dispatch(superModelActions.getFormDropDownNextPage());
    }
  };

  const onSubmit = () => dispatch(superModelActions.addSuperModel());

  return {
    t,
    open,
    form,
    saving,
    fields,
    isValid,
    addTitle: t(`${P}.ADD_TITLE`),
    onClose,
    setField,
    placeholder,
    fieldLabel,
    optionLabel,
    isNumberField,
    onManufacturerMenuScroll,
    onSubmit,
  };
};
